/**
 * Cross-org conversation list for the chat sidebar.
 *
 * Extension of flock-expo/src/hooks/useAllGroupsUnread.ts (the proven
 * cross-org listener pattern) that also surfaces lastMessage previews and
 * DM partner names so one hook powers the whole sidebar.
 *
 * Firestore read rules are membership-doc gated (NOT org-claim gated), so a
 * single client can listen to conversations across every org the user
 * belongs to. Only message SENDS require the org custom claim to match —
 * the UI switches org context when activating a cross-org conversation.
 *
 * Listener topology (mirrors mobile):
 *   1 membership query (chatGroupMembers by firebaseUid, cross-org)
 *   + N chatGroup doc listeners (incl. synthetic everyone_{groupId} docs)
 *   + N read-receipt listeners (with permission-denied retry/backoff)
 *   + getCountFromServer per unread conversation on recompute
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  collection,
  doc,
  getCountFromServer,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  where,
} from 'firebase/firestore';
import { auth, db } from '@/config/firebase';
import { useAuth } from '@/context/AuthContext';
import { normalizeUnreadCount } from '@/lib/chatUnread';

const CHAT_GROUPS_COLLECTION = 'chatGroups';
const CHAT_GROUP_MEMBERS_COLLECTION = 'chatGroupMembers';
const READ_RECEIPTS_COLLECTION = 'read_receipts';
const MESSAGES_COLLECTION = 'messages';

export interface Conversation {
  /** Stable key: chatGroupId for named groups/DMs, `everyone_{groupId}` for Everyone. */
  key: string;
  type: 'everyone' | 'group' | 'dm';
  /** null for the "Everyone" chat (messages carry chatGroupId == null). */
  chatGroupId: string | null;
  /** Parent community group. */
  groupId: string;
  groupName: string;
  organizationId: string;
  organizationName: string;
  /** Display name: "Everyone" / chat group name / DM partner name. */
  name: string;
  avatarUrl?: string;
  memberCount?: number;
  isOwner: boolean;
  addMembersRestricted?: boolean;
  ownerUserId?: string;
  dmPartnerUserId?: string;
  lastMessageText: string | null;
  lastMessageUserName: string | null;
  /** ISO string (chatGroups docs store ISO strings, not Timestamps). */
  lastMessageAt: string | null;
  unreadCount: number;
}

export interface UseAllConversationsResult {
  conversations: Conversation[];
  totalUnread: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

/** 500ms grace — receipts use message server timestamps, not client time. */
function isUnread(lastMessageAt: string, lastReadAt: string | null): boolean {
  if (!lastReadAt) return true;
  const lastReadTime = new Date(lastReadAt).getTime();
  const lastMsgTime = new Date(lastMessageAt).getTime();
  return lastMsgTime > lastReadTime + 500;
}

function messagePreview(data: any): string {
  const text = data.content?.text;
  if (text) return text.length > 100 ? text.substring(0, 100) + '...' : text;
  if (data.type === 'image') return 'Sent an image';
  if (data.type === 'gif') return 'Sent a GIF';
  if (data.type === 'file') return 'Sent a file';
  return 'Sent a message';
}

export function useAllConversations(): UseAllConversationsResult {
  const { organizations, isSwitchingOrg, isAuthenticated } = useAuth();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const mountedRef = useRef(true);
  const childUnsubsRef = useRef<(() => void)[]>([]);

  // Force re-subscription when the tab becomes visible again — listeners can
  // go stale while the tab is hidden (mobile uses AppState for the same fix).
  const [visibilityCount, setVisibilityCount] = useState(0);
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        setVisibilityCount((c) => c + 1);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // groupId → context (group/org names) across all orgs
  const groupContext = new Map<string, { groupName: string; orgId: string; orgName: string }>();
  for (const org of organizations) {
    for (const group of org.groups) {
      groupContext.set(group.id, {
        groupName: group.name,
        orgId: org.organization_id,
        orgName: org.organization.name,
      });
    }
  }
  const groupIdsKey = Array.from(groupContext.keys()).sort().join(',');

  const refresh = useCallback(() => setRefreshTrigger((c) => c + 1), []);

  useEffect(() => {
    const firebaseUid = auth.currentUser?.uid;
    if (!isAuthenticated || !firebaseUid || groupContext.size === 0) {
      setConversations([]);
      setLoading(false);
      return;
    }

    mountedRef.current = true;
    setLoading(true);
    setError(null);

    // Live stores mutated by listeners
    const convData = new Map<string, Conversation>();
    const receiptData = new Map<string, string | null | 'pending'>();
    let recomputeGen = 0;

    function pushConversations() {
      if (!mountedRef.current) return;
      const list = Array.from(convData.values()).sort((a, b) => {
        const aTime = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
        const bTime = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
        return bTime - aTime;
      });
      setConversations(list);
    }

    /** Recompute unread counts for every conversation (generation-guarded). */
    async function recompute() {
      if (!mountedRef.current || isSwitchingOrg) return;
      const gen = ++recomputeGen;

      const counts = new Map<string, number>();
      const countPromises: Promise<void>[] = [];

      for (const [key, conv] of convData) {
        if (!conv.lastMessageAt) {
          counts.set(key, 0);
          continue;
        }
        const lastReadAt = receiptData.get(key);
        if (lastReadAt === 'pending') continue; // retry in flight — don't flash a bad count
        if (!isUnread(conv.lastMessageAt, lastReadAt ?? null)) {
          counts.set(key, 0);
          continue;
        }

        countPromises.push(
          (async () => {
            try {
              // +1ms buffer: receipt ISO strings have ms precision, message
              // createdAt has µs precision — without it the reader's own last
              // message can count as unread.
              const lastReadTime = lastReadAt
                ? new Date(new Date(lastReadAt).getTime() + 1)
                : new Date(0);
              const countQuery =
                conv.type === 'everyone'
                  ? query(
                      collection(db, MESSAGES_COLLECTION),
                      where('chatGroupId', '==', null),
                      where('groupId', '==', conv.groupId),
                      where('organizationId', '==', conv.organizationId),
                      where('isDeleted', '==', false),
                      where('createdAt', '>', Timestamp.fromDate(lastReadTime)),
                      orderBy('createdAt', 'desc')
                    )
                  : query(
                      collection(db, MESSAGES_COLLECTION),
                      where('chatGroupId', '==', conv.chatGroupId),
                      where('organizationId', '==', conv.organizationId),
                      where('isDeleted', '==', false),
                      where('createdAt', '>', Timestamp.fromDate(lastReadTime)),
                      orderBy('createdAt', 'desc')
                    );
              const snap = await getCountFromServer(countQuery);
              counts.set(key, normalizeUnreadCount(snap.data().count));
            } catch (err) {
              console.error('[useAllConversations] count query failed for', key, err);
              counts.set(key, 1); // binary fallback
            }
          })()
        );
      }

      await Promise.all(countPromises);
      if (!mountedRef.current || gen !== recomputeGen) return;

      let changed = false;
      for (const [key, count] of counts) {
        const conv = convData.get(key);
        if (conv && conv.unreadCount !== count) {
          convData.set(key, { ...conv, unreadCount: count });
          changed = true;
        }
      }
      if (changed) pushConversations();
    }

    function buildConversation(key: string, ctx: NonNullable<ReturnType<typeof groupContext.get>>, groupId: string, data: any | null): Conversation {
      const prev = convData.get(key);
      const isEveryone = key.startsWith('everyone_');

      if (isEveryone) {
        let lastMessageAt: string | null = null;
        if (data?.lastMessageAt) {
          lastMessageAt =
            typeof data.lastMessageAt === 'string'
              ? data.lastMessageAt
              : data.lastMessageAt.toDate?.().toISOString() || null;
        }
        return {
          key,
          type: 'everyone',
          chatGroupId: null,
          groupId,
          groupName: ctx.groupName,
          organizationId: data?.organizationId || ctx.orgId,
          organizationName: ctx.orgName,
          name: 'Everyone',
          isOwner: false,
          lastMessageText: data?.lastMessageText ?? prev?.lastMessageText ?? null,
          lastMessageUserName: data?.lastMessageUserName ?? prev?.lastMessageUserName ?? null,
          lastMessageAt: lastMessageAt ?? prev?.lastMessageAt ?? null,
          unreadCount: prev?.unreadCount ?? 0,
        };
      }

      // Named chat group or DM — resolve partner identity for DMs
      const firebaseUid = auth.currentUser?.uid;
      let dmPartnerName: string | undefined;
      let dmPartnerAvatarUrl: string | undefined;
      let dmPartnerUserId: string | undefined;
      if (data.type === 'dm' && data.dmParticipants && firebaseUid) {
        for (const [uid, participant] of Object.entries(data.dmParticipants)) {
          if (uid !== firebaseUid) {
            const p = participant as any;
            dmPartnerName = p.name;
            dmPartnerAvatarUrl = p.profileImageUrl || undefined;
            dmPartnerUserId = p.userId;
            break;
          }
        }
      }

      return {
        key,
        type: data.type === 'dm' ? 'dm' : 'group',
        chatGroupId: key,
        groupId,
        groupName: ctx.groupName,
        organizationId: data.organizationId || ctx.orgId,
        organizationName: ctx.orgName,
        name: data.type === 'dm' ? dmPartnerName || 'Direct Message' : data.name,
        avatarUrl: data.type === 'dm' ? dmPartnerAvatarUrl : data.avatarUrl || undefined,
        memberCount: data.memberIds?.length || 0,
        isOwner: data.ownerFirebaseUid === firebaseUid,
        addMembersRestricted: data.addMembersRestricted || false,
        ownerUserId: data.ownerUserId,
        dmPartnerUserId,
        lastMessageText: data.lastMessageText ?? null,
        lastMessageUserName: data.lastMessageUserName ?? null,
        lastMessageAt: data.lastMessageAt ?? null,
        unreadCount: convData.get(key)?.unreadCount ?? 0,
      };
    }

    // ── Membership listener (cross-org) ──────────────────────────────────
    const membershipQuery = query(
      collection(db, CHAT_GROUP_MEMBERS_COLLECTION),
      where('firebaseUid', '==', firebaseUid),
      where('status', '==', 'active')
    );

    const unsubMembers = onSnapshot(
      membershipQuery,
      (memberSnapshot) => {
        if (!mountedRef.current) return;

        const entries: Array<{ key: string; groupId: string }> = [];
        memberSnapshot.forEach((docSnap) => {
          const data = docSnap.data();
          // Only include chat groups whose parent community group the user
          // still belongs to (context map covers all orgs).
          if (data.chatGroupId && data.groupId && groupContext.has(data.groupId)) {
            entries.push({ key: data.chatGroupId, groupId: data.groupId });
          }
        });

        // Synthetic Everyone entry per community group — sourced from the
        // org data (a user with no named chat groups still has Everyone).
        for (const gId of groupContext.keys()) {
          entries.push({ key: `everyone_${gId}`, groupId: gId });
        }

        // Reset child listeners
        childUnsubsRef.current.forEach((unsub) => unsub());
        childUnsubsRef.current = [];
        convData.clear();
        receiptData.clear();

        if (entries.length === 0) {
          setConversations([]);
          setLoading(false);
          return;
        }

        let initialResolved = 0;
        const total = entries.length * 2; // chatGroup doc + receipt each

        function checkInitialDone() {
          initialResolved++;
          if (initialResolved >= total && mountedRef.current) {
            setLoading(false);
            pushConversations();
            recompute();
          }
        }

        // Pre-seed Everyone rows so every community group always shows its
        // Everyone conversation even before any message exists (the synthetic
        // everyone_{gid} chatGroups doc is only created by the Cloud Function
        // after the first processed message — a missing doc would otherwise
        // permission-deny the listener and drop the row entirely).
        for (const { key, groupId } of entries) {
          if (key.startsWith('everyone_')) {
            const ctx = groupContext.get(groupId)!;
            convData.set(key, buildConversation(key, ctx, groupId, null));
          }
        }

        for (const { key, groupId } of entries) {
          const ctx = groupContext.get(groupId)!;
          const isEveryone = key.startsWith('everyone_');

          if (isEveryone) {
            // Live preview via latest-message query (mobile useChatGroups
            // pattern) — works whether or not the synthetic doc exists.
            const latestQuery = query(
              collection(db, MESSAGES_COLLECTION),
              where('chatGroupId', '==', null),
              where('groupId', '==', groupId),
              where('organizationId', '==', ctx.orgId),
              where('isDeleted', '==', false),
              orderBy('createdAt', 'desc'),
              limit(1)
            );
            const unsubLatest = onSnapshot(
              latestQuery,
              (snap) => {
                if (!mountedRef.current) return;
                const docSnap = snap.docs[0];
                const conv = convData.get(key);
                if (docSnap && conv) {
                  const data = docSnap.data();
                  const createdAt = data.createdAt;
                  const messageAt =
                    createdAt && typeof createdAt === 'object' && createdAt.toDate
                      ? createdAt.toDate().toISOString()
                      : typeof createdAt === 'string'
                        ? createdAt
                        : null;
                  // Pending serverTimestamp snapshots return null createdAt —
                  // skip; the resolved snapshot follows immediately.
                  if (messageAt) {
                    convData.set(key, {
                      ...conv,
                      lastMessageText: messagePreview(data),
                      lastMessageUserName: data.userName || null,
                      lastMessageAt: messageAt,
                    });
                  }
                }
                if (initialResolved < total) {
                  checkInitialDone();
                } else {
                  pushConversations();
                  recompute();
                }
              },
              (err: any) => {
                // permission-denied is expected for pending members
                if (err?.code !== 'permission-denied') {
                  console.error('[useAllConversations] Everyone preview listener error:', err);
                }
                if (initialResolved < total) checkInitialDone();
              }
            );
            childUnsubsRef.current.push(unsubLatest);
          } else {
            // chatGroup doc listener (lastMessage*, name, members, DM partner)
            const cgRef = doc(db, CHAT_GROUPS_COLLECTION, key);
            const unsubCg = onSnapshot(
              cgRef,
              (cgSnap) => {
                if (!mountedRef.current) return;

                if (cgSnap.exists() && cgSnap.data().status === 'active') {
                  convData.set(key, buildConversation(key, ctx, groupId, cgSnap.data()));
                } else {
                  convData.delete(key);
                }

                if (initialResolved < total) {
                  checkInitialDone();
                } else {
                  pushConversations();
                  recompute();
                }
              },
              (err: any) => {
                if (err?.code !== 'permission-denied') {
                  console.error('[useAllConversations] chatGroup listener error:', err);
                }
                if (initialResolved < total) checkInitialDone();
              }
            );
            childUnsubsRef.current.push(unsubCg);
          }

          // Read receipt listener with permission-denied retry/backoff
          const receiptId = isEveryone
            ? `${firebaseUid}_${groupId}`
            : `${firebaseUid}_${groupId}_${key}`;
          const receiptRef = doc(db, READ_RECEIPTS_COLLECTION, receiptId);

          function subscribeReceipt(attempt = 0) {
            const unsub = onSnapshot(
              receiptRef,
              (receiptSnap) => {
                if (!mountedRef.current) return;
                if (receiptSnap.exists()) {
                  const lastReadAt = receiptSnap.data().lastReadAt;
                  receiptData.set(
                    key,
                    typeof lastReadAt === 'string'
                      ? lastReadAt
                      : lastReadAt?.toDate?.().toISOString() || null
                  );
                } else {
                  receiptData.set(key, null);
                }
                if (initialResolved < total) {
                  checkInitialDone();
                } else {
                  recompute();
                }
              },
              (err: any) => {
                if (err?.code !== 'permission-denied') {
                  console.error('[useAllConversations] receipt listener error:', err);
                }
                receiptData.set(key, 'pending');
                if (initialResolved < total) checkInitialDone();
                if (attempt < 3 && mountedRef.current) {
                  const delay = Math.pow(2, attempt + 1) * 1000;
                  const retryTimer = setTimeout(() => {
                    if (mountedRef.current) subscribeReceipt(attempt + 1);
                  }, delay);
                  childUnsubsRef.current.push(() => clearTimeout(retryTimer));
                }
              }
            );
            childUnsubsRef.current.push(unsub);
          }
          subscribeReceipt();
        }
      },
      (err: any) => {
        if (!mountedRef.current) return;
        if (err?.code === 'permission-denied') {
          setError('Unable to load conversations. Try signing out and back in.');
        } else {
          console.error('[useAllConversations] membership listener error:', err);
          setError("Couldn't load conversations. Try again?");
        }
        setLoading(false);
      }
    );

    return () => {
      mountedRef.current = false;
      unsubMembers();
      childUnsubsRef.current.forEach((unsub) => unsub());
      childUnsubsRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupIdsKey, isAuthenticated, isSwitchingOrg, visibilityCount, refreshTrigger]);

  const totalUnread = conversations.reduce((sum, c) => sum + c.unreadCount, 0);

  return { conversations, totalUnread, loading, error, refresh };
}

export default useAllConversations;
